// Catalog.ID Client — `bitcash-wallets-save` ephemeral keypair store
// ============================================================================
// The hybrid analogue of `save-store.ts`, for the two-leg save that brings ONE
// wallet from the bitcash manager into the member's wallet document
// (cid-encrypted-storage §7.3).
//
// Leg 1 (`bitcash-wallets-save-prepare`) mints a hybrid X25519 + ML-KEM-1024
// keypair, answers with the public half and stashes the private half under the
// PREPARE's requestId. Leg 2 (`bitcash-wallets-save`) names that requestId,
// and the stash hands back the secret so the wrapped wallet can be opened.
//
// Why not reuse `save-store.ts`: that one mints P-256 for the legacy key-grant
// `save` flow. This family is hybrid everywhere else (the release wrap, every
// mail and chat wrap), and mixing a classical-only keypair into it would make
// the weakest link of a post-quantum design a 2026 addition.
//
// THE STASH RECORDS THE REQUESTER, and leg 2 is refused when it disagrees. The
// requestId is 128 bits of randomness the module chose, so this is belt and
// braces on top of the hub stamp — but a prepare leg is answered to ANY
// registered module that asks, and without this check a second module that
// guessed a requestId could spend the first one's ephemeral key.
//
// All in memory, never persisted. A tab reload drops everything, which is the
// desired behaviour: a save whose prepare leg is gone must start again.

import { kemKeygen, type KemKeyPair } from '../crypto/kem';

/** Matches `save-store.ts`: long enough for a user-visible step, short enough
 *  that a forgotten leg does not keep a secret alive for a session. */
const TTL_MS = 5 * 60 * 1000;

interface StashedEntry {
    secretKey: Uint8Array;
    publicKey: Uint8Array;
    expiresAt: number;
    requesterId: string;
}

const store = new Map<string, StashedEntry>();

let cleanupTimer: ReturnType<typeof setInterval> | null = null;

function ensureCleanupTimer(): void {
    if (cleanupTimer) return;
    cleanupTimer = setInterval(() => {
        const now = Date.now();
        for (const [k, v] of store) {
            if (v.expiresAt <= now) {
                v.secretKey.fill(0);
                store.delete(k);
            }
        }
    }, 30_000);
}

/**
 * Mint a fresh hybrid keypair for `requestId` and return its 1600-byte public
 * half. The caller answers with it; leg 2 reaches the secret through
 * {@link consumeWalletSavePrepare}.
 */
export function prepareWalletSave(
    requestId: string, requesterId: string, keygen?: () => KemKeyPair,
): Uint8Array {
    ensureCleanupTimer();
    const pair = (keygen ?? kemKeygen)();
    // A second prepare under the same requestId replaces the first; the
    // superseded secret is zeroed rather than left to the sweeper.
    store.get(requestId)?.secretKey.fill(0);
    store.set(requestId, {
        secretKey: pair.secretKey,
        publicKey: pair.publicKey,
        expiresAt: Date.now() + TTL_MS,
        requesterId,
    });
    return pair.publicKey;
}

/**
 * Look up and REMOVE the entry for `requestId`. Null when it is missing or
 * expired — one prepare leg opens exactly one save, so a replayed leg 2 finds
 * nothing.
 */
export function consumeWalletSavePrepare(
    requestId: string, now: number = Date.now(),
): { secretKey: Uint8Array; publicKey: Uint8Array; requesterId: string } | null {
    const entry = store.get(requestId);
    if (!entry) return null;
    store.delete(requestId);
    if (entry.expiresAt <= now) {
        entry.secretKey.fill(0);
        return null;
    }
    return { secretKey: entry.secretKey, publicKey: entry.publicKey, requesterId: entry.requesterId };
}

export function walletSaveStashSize(): number { return store.size; }

/** Drop every in-flight prepare. Called on logout / lock. */
export function clearWalletSaveStash(): void {
    for (const [, v] of store) v.secretKey.fill(0);
    store.clear();
}
